import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse, type CancelTokenSource } from 'axios';
import type { 
  RequestConfig, 
  ApiResponse, 
  //ApiErrorResponse, 
  ApiClientConfig,
  RequestInterceptor,
  ResponseInterceptor 
} from '../../types/api/common';
import { createApiConfig, TIMEOUT_CONFIG } from './ApiConfig';
import { handleMockRequest, isMockApiEnabled } from '../../mocks/mockApi';
import { ResponseHandler } from './ResponseHandler';
import { RequestInterceptorImpl } from './RequestInterceptor';
import { ResponseInterceptorImpl } from './ResponseInterceptor';
// import { ResponseHandler } from './ResponseHandler';
// import { RequestInterceptorImpl } from './RequestInterceptor';
// import { ResponseInterceptorImpl } from './ResponseInterceptor';

/**
 * Advanced HTTP Client with comprehensive features:
 * - Request/Response interceptors
 * - Automatic retries with exponential backoff
 * - Request deduplication
 * - Caching mechanism
 * - Token management
 * - Request cancellation
 * - Performance monitoring
 */
export class HttpClient {
  private static instance: HttpClient;
  private axiosInstance: AxiosInstance;
  private config: ApiClientConfig;
  private responseHandler: ResponseHandler;
  private requestInterceptor: RequestInterceptorImpl;
  private responseInterceptor: ResponseInterceptorImpl;
  private pendingRequests: Map<string, CancelTokenSource> = new Map();
  private requestCache: Map<string, { data: any; timestamp: number; ttl: number }> = new Map();
  private performanceMetrics: Map<string, number> = new Map();

  private constructor(config?: Partial<ApiClientConfig>) {
    this.config = { ...createApiConfig(), ...config };
    this.axiosInstance = this.createAxiosInstance();
    this.responseHandler = new ResponseHandler();
    this.requestInterceptor = new RequestInterceptorImpl();
    this.responseInterceptor = new ResponseInterceptorImpl();
    
    // Set axios instance for token refresh in response interceptor
    this.responseInterceptor.setAxiosInstance(this.axiosInstance);
    
    this.setupInterceptors();
    this.setupRequestDeduplication();
    this.startCacheCleanup();
  }

  /**
   * Get singleton instance of HttpClient
   */
  public static getInstance(config?: Partial<ApiClientConfig>): HttpClient {
    if (!HttpClient.instance) {
      HttpClient.instance = new HttpClient(config);
    }
    return HttpClient.instance;
  }

  /**
   * Create axios instance with base configuration
   */
  private createAxiosInstance(): AxiosInstance {
    // Do NOT set a default Content-Type at the instance level, or it will override
    // per-request headers and break FormData uploads. Keep other defaults.
    const defaultHeaders = { ...this.config.headers } as Record<string, any>;
    delete defaultHeaders['Content-Type'];
    delete defaultHeaders['content-type'];

    const instance = axios.create({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
      headers: defaultHeaders,
    });

    return instance;
  }

  /**
   * Setup request and response interceptors
   */
  private setupInterceptors(): void {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      (config) => this.requestInterceptor.onFulfilled(config),
      (error) => this.requestInterceptor.onRejected(error)
    );

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response) => this.responseInterceptor.onFulfilled(response),
      (error) => this.responseInterceptor.onRejected(error)
    );
  }

  /**
   * Setup request deduplication to prevent duplicate concurrent requests
   */
  private setupRequestDeduplication(): void {
    this.axiosInstance.interceptors.request.use((config) => {
      const requestKey = this.generateRequestKey(config);
      
      // Cancel previous request if it's still pending
      if (this.pendingRequests.has(requestKey)) {
        const cancelToken = this.pendingRequests.get(requestKey);
        cancelToken?.cancel('Request superseded by newer request');
      }

      // Create new cancel token for this request
      const source = axios.CancelToken.source();
      config.cancelToken = source.token;
      this.pendingRequests.set(requestKey, source);

      return config;
    });

    this.axiosInstance.interceptors.response.use(
      (response) => {
        const requestKey = this.generateRequestKey(response.config);
        this.pendingRequests.delete(requestKey);
        return response;
      },
      (error) => {
        if (error.config) {
          const requestKey = this.generateRequestKey(error.config);
          this.pendingRequests.delete(requestKey);
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Generate unique key for request identification
   */
  private generateRequestKey(config: AxiosRequestConfig): string {
    const { method = 'GET', url = '', data, params } = config;
    const dataStr = data ? JSON.stringify(data) : '';
    const paramsStr = params ? JSON.stringify(params) : '';
    return `${method.toUpperCase()}:${url}:${dataStr}:${paramsStr}`;
  }

  /**
   * Start periodic cache cleanup
   */
  private startCacheCleanup(): void {
    setInterval(() => {
      const now = Date.now();
      for (const [key, entry] of this.requestCache.entries()) {
        if (now - entry.timestamp > entry.ttl) {
          this.requestCache.delete(key);
        }
      }
    }, 60000); // Clean up every minute
  }

  /**
   * Check if request should be cached
   */
  private shouldCache(config: RequestConfig): boolean {
    return (
      this.config.cache.enabled &&
      config.cache !== false &&
      config.method === 'GET'
    );
  }

  /**
   * Get cached response if available and valid
   */
  private getCachedResponse<T>(requestKey: string): ApiResponse<T> | null {
    if (!this.config.cache.enabled) return null;

    const cached = this.requestCache.get(requestKey);
    if (!cached) return null;

    const now = Date.now();
    if (now - cached.timestamp > cached.ttl) {
      this.requestCache.delete(requestKey);
      return null;
    }

    return cached.data;
  }

  /**
   * Cache response
   */
  private setCachedResponse(requestKey: string, data: any, ttl: number): void {
    if (!this.config.cache.enabled) return;

    // Implement LRU cache behavior
    if (this.requestCache.size >= this.config.cache.maxSize) {
      const firstKey = this.requestCache.keys().next().value;
      if (firstKey) {
        this.requestCache.delete(firstKey);
      }
    }

    this.requestCache.set(requestKey, {
      data,
      timestamp: Date.now(),
      ttl,
    });
  }

  /**
   * Perform HTTP request with advanced features
   */
  public async request<T = any>(config: RequestConfig): Promise<ApiResponse<T>> {
    if (isMockApiEnabled()) {
      return handleMockRequest<T>(config);
    }

    const startTime = performance.now();
    const requestKey = this.generateRequestKey(config as AxiosRequestConfig);
    const method = (config.method || 'GET').toUpperCase();

    try {
      // Check cache first
      if (this.shouldCache(config)) {
        const cachedResponse = this.getCachedResponse<T>(requestKey);
        if (cachedResponse) {
          return cachedResponse;
        }
      }

      // Prepare axios config
      // For FormData, we need to ensure Content-Type is NOT set so axios can set it automatically
      // Axios will detect FormData and set: Content-Type: multipart/form-data; boundary=...
      const isFormData = this.isFormData(config.data);
      
      // Build headers, but exclude Content-Type for FormData
      // IMPORTANT: For FormData, we must NOT set Content-Type - axios will set it automatically
      const mergedHeaders: Record<string, string> = {};
      
      // Copy default headers (excluding Content-Type for FormData)
      Object.keys(this.config.headers).forEach((key) => {
        if (isFormData && (key.toLowerCase() === 'content-type')) {
          // Skip Content-Type for FormData - let axios set it automatically
          return;
        }
        mergedHeaders[key] = this.config.headers[key];
      });
      
      // Copy config headers (excluding Content-Type for FormData)
      if (config.headers) {
        Object.keys(config.headers).forEach((key) => {
          if (isFormData && (key.toLowerCase() === 'content-type')) {
            // Skip Content-Type for FormData - let axios set it automatically
            return;
          }
          mergedHeaders[key] = (config.headers as any)[key];
        });
      }
      
      // For FormData, ensure headers object doesn't have Content-Type
      // Axios will automatically detect FormData and set: Content-Type: multipart/form-data; boundary=...
      // CRITICAL: If Content-Type is present in headers, axios won't override it
      const finalHeaders = isFormData 
        ? { ...mergedHeaders } // Create a new object to ensure no Content-Type
        : mergedHeaders;
      
      // Explicitly remove Content-Type for FormData (double-check)
      if (isFormData) {
        delete finalHeaders['Content-Type'];
        delete finalHeaders['content-type'];
      }
      
      // For non-FormData JSON requests, ensure Content-Type is application/json
      if (!isFormData) {
        if (!('Content-Type' in finalHeaders) && !('content-type' in finalHeaders)) {
          finalHeaders['Content-Type'] = 'application/json';
        }
      }

      const axiosConfig: AxiosRequestConfig = {
        method: config.method,
        url: config.url,
        data: config.data,
        params: config.params,
        headers: finalHeaders,
        timeout: config.timeout || this.getTimeoutForRequest(config.url),
      };
      
      // Final check: For FormData, ensure Content-Type is not in headers
      // Axios will automatically detect FormData and set the correct Content-Type with boundary
      if (isFormData && axiosConfig.headers) {
        // Remove Content-Type if it somehow got added
        if (typeof axiosConfig.headers.delete === 'function') {
          // AxiosHeaders instance
          axiosConfig.headers.delete('Content-Type');
          axiosConfig.headers.delete('content-type');
        } else {
          // Plain object
          delete (axiosConfig.headers as any)['Content-Type'];
          delete (axiosConfig.headers as any)['content-type'];
        }
      }

      // Perform request with retry logic
      const response = await this.performRequestWithRetry<T>(axiosConfig);
      
      // Process response
      const processedResponse = this.responseHandler.handleSuccess<T>(response);

      // If this was a write operation, clear the GET cache so subsequent
      // reads always see fresh data.
      if (method !== 'GET') {
        this.clearCache();
      }

      // Cache GET responses if applicable
      if (method === 'GET' && this.shouldCache(config)) {
        const ttl = config.cacheTTL || this.config.cache.ttl;
        this.setCachedResponse(requestKey, processedResponse, ttl);
      }

      // Record performance metrics
      this.recordPerformanceMetric(requestKey, performance.now() - startTime);

      return processedResponse;
    } catch (error) {
      // Record performance metrics for failed requests
      this.recordPerformanceMetric(requestKey, performance.now() - startTime);
      
      // Handle error
      throw this.responseHandler.handleError(error);
    }
  }

  /**
   * Robust FormData detection to handle polyfills/different globals
   */
  private isFormData(data: unknown): data is FormData {
    if (!data) return false;
    if (typeof FormData !== 'undefined' && data instanceof FormData) {
      return true;
    }
    const tag = Object.prototype.toString.call(data);
    if (tag === '[object FormData]') {
      return true;
    }
    const ctorName = (data as any)?.constructor?.name;
    if (ctorName === 'FormData' && typeof (data as any).append === 'function') {
      return true;
    }
    return false;
  }

  /**
   * Perform request with retry logic
   */
  private async performRequestWithRetry<T>(
    config: AxiosRequestConfig,
    attempt: number = 1
  ): Promise<AxiosResponse<T>> {
    try {
      return await this.axiosInstance.request<T>(config);
    } catch (error) {
      const shouldRetry = this.shouldRetryRequest(error, attempt);
      
      if (shouldRetry) {
        const delay = this.calculateRetryDelay(attempt);
        await this.sleep(delay);
        return this.performRequestWithRetry<T>(config, attempt + 1);
      }
      
      throw error;
    }
  }

  /**
   * Determine if request should be retried
   */
  private shouldRetryRequest(error: any, attempt: number): boolean {
    if (attempt >= this.config.retry.attempts) return false;
    if (axios.isCancel(error)) return false;
    
    return this.config.retry.retryCondition(error);
  }

  /**
   * Calculate retry delay with exponential backoff
   */
  private calculateRetryDelay(attempt: number): number {
    const baseDelay = this.config.retry.delay;
    const backoffFactor = this.config.retry.backoffFactor;
    return baseDelay * Math.pow(backoffFactor, attempt - 1);
  }

  /**
   * Sleep utility for retry delays
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get appropriate timeout for request based on URL
   */
  private getTimeoutForRequest(url?: string): number {
    if (!url) return TIMEOUT_CONFIG.DEFAULT;
    
    if (url.includes('/upload')) return TIMEOUT_CONFIG.UPLOAD;
    if (url.includes('/payment')) return TIMEOUT_CONFIG.PAYMENT;
    if (url.includes('/search')) return TIMEOUT_CONFIG.SEARCH;
    
    return TIMEOUT_CONFIG.DEFAULT;
  }

  /**
   * Record performance metrics
   */
  private recordPerformanceMetric(requestKey: string, duration: number): void {
    this.performanceMetrics.set(requestKey, duration);
    
    // Keep only last 1000 metrics to prevent memory leaks
    if (this.performanceMetrics.size > 1000) {
      const firstKey = this.performanceMetrics.keys().next().value;
      if (firstKey) {
        this.performanceMetrics.delete(firstKey);
      }
    }
  }

  /**
   * Convenience methods for common HTTP verbs
   */
  public async get<T = any>(url: string, config?: Partial<RequestConfig>): Promise<ApiResponse<T>> {
    return this.request<T>({ method: 'GET', url, ...config, withCredentials: false });
  }

  public async post<T = any>(url: string, data?: any, config?: Partial<RequestConfig>): Promise<ApiResponse<T>> {
    return this.request<T>({ method: 'POST', url, data, ...config, withCredentials: false });
  }

  public async put<T = any>(url: string, data?: any, config?: Partial<RequestConfig>): Promise<ApiResponse<T>> {
    return this.request<T>({ method: 'PUT', url, data, ...config, withCredentials: false });
  }

  public async patch<T = any>(url: string, data?: any, config?: Partial<RequestConfig>): Promise<ApiResponse<T>> {
    return this.request<T>({ method: 'PATCH', url, data, ...config, withCredentials: false });
  }

  public async delete<T = any>(url: string, config?: Partial<RequestConfig>): Promise<ApiResponse<T>> {
    return this.request<T>({ method: 'DELETE', url, ...config, withCredentials: false });
  }

  /**
   * Add request interceptor
   */
  public addRequestInterceptor(interceptor: RequestInterceptor): void {
    this.config.interceptors.request.push(interceptor);
  }

  /**
   * Add response interceptor
   */
  public addResponseInterceptor(interceptor: ResponseInterceptor): void {
    this.config.interceptors.response.push(interceptor);
  }

  /**
   * Cancel all pending requests
   */
  public cancelAllRequests(reason?: string): void {
    for (const [key, source] of this.pendingRequests.entries()) {
      source.cancel(reason || 'Request cancelled');
      this.pendingRequests.delete(key);
    }
  }

  /**
   * Cancel specific request by key
   */
  public cancelRequest(requestKey: string, reason?: string): void {
    const source = this.pendingRequests.get(requestKey);
    if (source) {
      source.cancel(reason || 'Request cancelled');
      this.pendingRequests.delete(requestKey);
    }
  }

  /**
   * Clear request cache
   */
  public clearCache(): void {
    this.requestCache.clear();
  }

  /**
   * Get performance metrics
   */
  public getPerformanceMetrics(): Map<string, number> {
    return new Map(this.performanceMetrics);
  }

  /**
   * Get cache statistics
   */
  public getCacheStats() {
    return {
      size: this.requestCache.size,
      maxSize: this.config.cache.maxSize,
      hitRate: 0, // Would need to track hits/misses for accurate calculation
    };
  }

  /**
   * Update configuration
   */
  public updateConfig(newConfig: Partial<ApiClientConfig>): void {
    this.config = { ...this.config, ...newConfig };
    
    // Update axios instance if base config changed
    if (newConfig.baseURL || newConfig.timeout || newConfig.headers) {
      this.axiosInstance.defaults.baseURL = this.config.baseURL;
      this.axiosInstance.defaults.timeout = this.config.timeout;
      this.axiosInstance.defaults.headers = { ...this.axiosInstance.defaults.headers, ...this.config.headers };
    }
  }

  /**
   * Get response interceptor instance
   */
  public getResponseInterceptor(): ResponseInterceptorImpl {
    return this.responseInterceptor;
  }

  /**
   * Get request interceptor instance
   */
  public getRequestInterceptor(): RequestInterceptorImpl {
    return this.requestInterceptor;
  }
}
